import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import api from "service/api";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Container,
  Row,
  Col,
  Table,
  Input,
  Label,
} from "reactstrap";
import Select from "react-select";
import { RemoveCircle } from "@mui/icons-material";
import newApi from "service/newApi";
import { CookieTool } from "utils/CookieTool";

const DataProcessingForm = () => {
  const { handleSubmit } = useForm();
  const [loading, setLoading] = useState(false);
  const [hasId, setHasId] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [dataConfigurations, setDataConfigurations] = useState([]);
  const [testResults, setTestResults] = useState({});
  const [hasExistingConfig, setHasExistingConfig] = useState(false);

  const userClientId = CookieTool.get("admin_client_id");
  const userClient = CookieTool.get("admin_client");

  useEffect(() => {
    loadClients();

    if (userClientId && userClient) {
      setSelectedClient({ value: userClientId, label: userClient });
      setHasId(true);
      loadDataProcessingConfig(userClientId);
    }
  }, []);

  const loadClients = async () => {
    try {
      const response = await api.get(`/clients?page=1&limit=10000`);
      setClients(
        response.data.clients.map((item) => ({
          label: item.name,
          value: item.id,
        })),
      );
    } catch (error) {
      console.error("Error loading clients:", error);
      toast.error("Erro ao recuperar clientes!");
    }
  };

  const loadDataProcessingConfig = async (clientId) => {
    try {
      const response = await newApi.get(
        `/v1/data-processing-config/${clientId}`,
      );

      if (response.data && response.data.dataFields) {
        setHasExistingConfig(true);
        setDataConfigurations(
          response.data.dataFields.map((field, index) => ({
            id: field.id || Date.now() + index,
            dataType: field.dataType,
            regexPattern: field.regexPattern,
            writeTo: field.redactedText,
            testValue: "",
          })),
        );
      } else {
        setHasExistingConfig(false);
        setDataConfigurations([]);
      }
    } catch (error) {
      console.error("Error loading data processing config:", error);

      if (error.response && error.response.status === 404) {
        setHasExistingConfig(false);
        setDataConfigurations([]);
        toast.info(
          "Este cliente ainda não possui configuração de processamento de dados. Você está criando a primeira configuração.",
        );
      } else {
        setHasExistingConfig(false);
        setDataConfigurations([]);
        toast.error("Erro ao carregar configuração do cliente!");
      }
    }
  };

  const emptyDataConfig = {
    id: Date.now(),
    dataType: "",
    regexPattern: "",
    writeTo: "",
    testValue: "",
  };

  const onSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        dataFields: dataConfigurations.map((config) => ({
          dataType: config.dataType,
          regexPattern: config.regexPattern,
          redactedText: config.writeTo,
        })),
      };

      let response;
      if (hasExistingConfig) {
        response = await newApi.put(
          `/v1/data-processing-config/${selectedClient?.value}`,
          payload,
        );
        toast.success(
          "Configuração de processamento de dados atualizada com sucesso!",
        );
      } else {
        const createPayload = {
          clientId: selectedClient?.value,
          ...payload,
        };
        response = await newApi.post(
          `/v1/data-processing-config`,
          createPayload,
        );
        setHasExistingConfig(true);
        toast.success(
          "Configuração de processamento de dados criada com sucesso!",
        );
      }

      console.log("API response:", response.data);
    } catch (error) {
      console.error("Error saving configuration:", error);
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        toast.error(`Erro: ${error.response.data.message}`);
      } else {
        toast.error("Falha ao salvar configuração. Tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClientChange = (selectedOption) => {
    setSelectedClient(selectedOption);
    if (selectedOption) {
      loadDataProcessingConfig(selectedOption.value);
    } else {
      setDataConfigurations([]);
      setHasExistingConfig(false);
    }
  };

  const handleDataConfigChange = (id, field, value) => {
    setDataConfigurations((prev) =>
      prev.map((config) =>
        config.id === id ? { ...config, [field]: value } : config,
      ),
    );
  };

  const addDataConfiguration = () => {
    setDataConfigurations((prev) => [
      ...prev,
      { ...emptyDataConfig, id: Date.now() },
    ]);
  };

  const removeDataConfiguration = (id) => {
    setDataConfigurations((prev) => prev.filter((config) => config.id !== id));
    setTestResults((prev) => {
      const newResults = { ...prev };
      delete newResults[id];
      return newResults;
    });
  };

  const testRegex = (id, pattern, testValue) => {
    if (!pattern || !testValue) {
      setTestResults((prev) => ({ ...prev, [id]: null }));
      return;
    }

    try {
      const regex = new RegExp(pattern, "g");
      const matches = testValue.match(regex);
      const hasMatch = matches !== null && matches.length > 0;

      setTestResults((prev) => ({
        ...prev,
        [id]: {
          match: hasMatch,
          extractedValue: hasMatch ? matches[0] : null,
          allMatches: matches || [],
          pattern: pattern,
        },
      }));
    } catch (error) {
      console.error("Regex error:", error, "Pattern:", pattern);
      setTestResults((prev) => ({
        ...prev,
        [id]: {
          error: `Padrão regex inválido: ${error.message}`,
          pattern: pattern,
        },
      }));
    }
  };

  useEffect(() => {
    if (userClientId) {
      setHasId(true);
      const client = clients.find((c) => c.value === userClientId);
      if (client) {
        setSelectedClient(client);
        loadDataProcessingConfig(userClientId);
      } else {
        setSelectedClient({ value: userClientId, label: userClient });
        loadDataProcessingConfig(userClientId);
      }
    }
  }, []);

  return (
    <Container className="mt-4" fluid>
      <Row>
        <Col>
          <Card className="shadow">
            <CardHeader className="bg-transparent">
              <Row className="align-items-center">
                <Col>
                  <h2 className="">Tratamento de dados do cliente</h2>
                </Col>
              </Row>
            </CardHeader>

            <CardBody>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                  <Col md="12">
                    <FormGroup>
                      <Label className="form-control-label">Cliente</Label>
                      <Select
                        placeholder="Selecione um cliente..."
                        value={selectedClient}
                        onChange={handleClientChange}
                        options={clients}
                        isDisabled={userClientId || hasId}
                        className="basic-single"
                        classNamePrefix="select"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {selectedClient && (
                  <>
                    <Row className="mt-4">
                      <Col>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h3 className="mb-0">Configuração</h3>
                          <Button
                            color="primary"
                            size="md"
                            onClick={addDataConfiguration}
                          >
                            + Adicionar novo dado
                          </Button>
                        </div>
                      </Col>
                    </Row>

                    {dataConfigurations.length === 0 ? (
                      <Row>
                        <Col>
                          <div className="text-center py-4">
                            <p className="text-muted mb-0">
                              Nenhuma configuração de processamento de dados
                              encontrada para este cliente.
                            </p>
                            <p className="text-muted">
                              Clique em &quot;Adicionar novo dado&quot; para
                              criar uma nova configuração.
                            </p>
                          </div>
                        </Col>
                      </Row>
                    ) : (
                      <Row>
                        <Col>
                          <Table
                            className="align-items-center table-flush"
                            responsive
                          >
                            <thead className="thead-light custom-head">
                              <tr>
                                <th>Tipo de Dado</th>
                                <th>Padrão Regex</th>
                                <th>Manter como</th>
                                <th>Realizar teste</th>
                                <th>Resultado</th>
                                <th>Remover</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dataConfigurations.map((config) => (
                                <tr key={config.id} className="custom-tr">
                                  <td>
                                    <Input
                                      type="text"
                                      value={config.dataType}
                                      onChange={(e) =>
                                        handleDataConfigChange(
                                          config.id,
                                          "dataType",
                                          e.target.value,
                                        )
                                      }
                                      placeholder="ex.: E-mail"
                                      className="custom-input"
                                      style={{ minWidth: "120px" }}
                                    />
                                  </td>
                                  <td>
                                    <Input
                                      type="text"
                                      value={config.regexPattern}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        handleDataConfigChange(
                                          config.id,
                                          "regexPattern",
                                          value,
                                        );
                                        testRegex(
                                          config.id,
                                          value,
                                          config.testValue,
                                        );
                                      }}
                                      placeholder="ex.: [a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
                                      title="Use uma única barra invertida (\) para caracteres especiais. Ex: \d para dígitos, \. para ponto literal"
                                      className="custom-input"
                                      style={{
                                        minWidth: "180px",
                                        fontFamily: "monospace",
                                      }}
                                    />
                                  </td>
                                  <td>
                                    <Input
                                      type="text"
                                      value={config.writeTo}
                                      onChange={(e) =>
                                        handleDataConfigChange(
                                          config.id,
                                          "writeTo",
                                          e.target.value,
                                        )
                                      }
                                      placeholder="ex.: <EMAIL>"
                                      className="custom-input"
                                      style={{ minWidth: "120px" }}
                                    />
                                  </td>
                                  <td>
                                    <Input
                                      type="text"
                                      value={config.testValue}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        handleDataConfigChange(
                                          config.id,
                                          "testValue",
                                          value,
                                        );
                                        testRegex(
                                          config.id,
                                          config.regexPattern,
                                          value,
                                        );
                                      }}
                                      placeholder="Digite um valor para teste"
                                      className="custom-input"
                                      style={{ minWidth: "150px" }}
                                    />
                                  </td>
                                  <td style={{ minWidth: "150px" }}>
                                    {testResults[config.id] ? (
                                      <div>
                                        {testResults[config.id].error ? (
                                          <span className="text-danger small">
                                            {testResults[config.id].error}
                                          </span>
                                        ) : testResults[config.id].match ? (
                                          <div>
                                            <span className="text-success small">
                                              ✓ Corresponde
                                            </span>
                                            {testResults[config.id]
                                              .extractedValue && (
                                              <div className="text-muted small mt-1">
                                                Capturado:{" "}
                                                <strong>
                                                  &quot;
                                                  {
                                                    testResults[config.id]
                                                      .extractedValue
                                                  }
                                                  &quot;
                                                </strong>
                                              </div>
                                            )}
                                            {testResults[config.id].allMatches
                                              .length > 1 && (
                                              <div className="text-info small">
                                                <div>
                                                  +{" "}
                                                  {testResults[config.id]
                                                    .allMatches.length - 1}{" "}
                                                  outras correspondências
                                                </div>
                                                <div
                                                  className="mt-1"
                                                  style={{
                                                    fontSize: "0.75rem",
                                                    maxHeight: "60px",
                                                    overflowY: "auto",
                                                  }}
                                                >
                                                  Todas:{" "}
                                                  {testResults[
                                                    config.id
                                                  ].allMatches.join(", ")}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          <span className="text-warning small">
                                            ✗ Não corresponde
                                          </span>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-muted small">
                                        Digite valores para testar
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    <RemoveCircle
                                      type="button"
                                      sx={{ width: "32px", marginLeft: "16px" }}
                                      onClick={() =>
                                        removeDataConfiguration(config.id)
                                      }
                                      style={{ color: "red" }}
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Col>
                      </Row>
                    )}

                    <Row className="mt-4">
                      <Col className="text-right">
                        <Button
                          color="success"
                          type="submit"
                          disabled={loading || !selectedClient}
                        >
                          {loading ? "Salvando..." : "Salvar Configurações"}
                        </Button>
                      </Col>
                    </Row>
                  </>
                )}
              </form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default DataProcessingForm;
